import { Fragment, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type WebhookDelivery, type WebhookEndpoint } from '../api';
import { CopyField, Icon, Tip, useNow, when } from '../ui';

const EVENT_HELP: Record<string, string> = {
  'stream.live': 'The publisher connected',
  'stream.ended': 'It was live and is over',
  'stream.expired': 'Nothing connected in 30 min',
  'stream.cancelled': 'Deleted before going live',
  'recording.ready': 'The MP4 can be downloaded',
  'recording.failed': 'No MP4 could be made',
  'recording.deleted': 'The MP4 was deleted',
  'credit.low': 'Credit dropped below your amount in Settings',
  'credit.depleted': 'Credit ran out: live streams stopped',
  'recordings.deletion_scheduled': 'Recordings will be deleted: 7 days at zero credit',
  'balance.negative': 'Postpaid: balance went below zero',
  'balance.statement': 'Postpaid: statement every 14 days while below zero',
  'balance.threshold': 'Postpaid: amount owed passed 100 / 500 / 1,000 USD',
};

type List = { data: WebhookEndpoint[]; event_types: string[] };

export function Webhooks() {
  const q = useQuery({ queryKey: ['webhooks'], queryFn: () => api<List>('/v1/webhooks') });
  return (
    <section className="page">
      <div>
        <h1>Webhooks</h1>
        <p className="sub">
          We POST to your server when a stream goes live or ends, when its recording is ready, and when your credit runs low. Signed with{' '}
          <a href="https://www.standardwebhooks.com" target="_blank" rel="noreferrer">
            Standard Webhooks
          </a>
          ; see <a href="https://docs.justonair.com/webhooks">the docs</a> for verifying them.
        </p>
      </div>
      {q.isPending ? (
        <p className="muted">Loading…</p>
      ) : q.error ? (
        <p className="error-text">{q.error.message}</p>
      ) : (
        <>
          {q.data.data.map((e) => (
            <EndpointCard key={e.id} endpoint={e} />
          ))}
          {q.data.data.length < 5 && <AddEndpoint types={q.data.event_types} first={q.data.data.length === 0} />}
        </>
      )}
    </section>
  );
}

function EventPicker({ types, value, onChange }: { types: string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="events-pick">
      {types.map((t) => (
        <label key={t} className="check" title={EVENT_HELP[t]}>
          <input type="checkbox" checked={value.includes(t)} onChange={(ev) => onChange(ev.target.checked ? [...value, t] : value.filter((x) => x !== t))} />
          {t}
        </label>
      ))}
    </div>
  );
}

function AddEndpoint({ types, first }: { types: string[]; first: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(first);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(types);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<WebhookEndpoint | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!events.length) return setError('Pick at least one event.');
    setBusy(true);
    setError(null);
    try {
      const ep = await api<WebhookEndpoint>('/v1/webhooks', { method: 'POST', json: { url: url.trim(), events } });
      setCreated(ep);
      setUrl('');
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['webhooks'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the endpoint.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {created?.secret && (
        <section className="card secret-panel">
          <div className="card-head">
            <h2 style={{ fontSize: 16 }}>Endpoint added. Put this secret on your server to verify each webhook.</h2>
            <button type="button" className="btn primary" onClick={() => setCreated(null)}>
              Done
            </button>
          </div>
          <CopyField label="Signing secret" value={created.secret} oneLine secret />
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>You can see it again on the endpoint. Send a test to check your handler.</p>
        </section>
      )}
      {open ? (
        <form className="card" onSubmit={submit}>
          <h2>{first ? 'Add your endpoint' : 'Add another endpoint'}</h2>
          <label className="field">
            URL
            <input className="input" type="url" required maxLength={2000} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.example.com/justonair/webhooks" />
          </label>
          <span className="faint" style={{ fontSize: 12 }}>Events</span>
          <EventPicker types={types} value={events} onChange={setEvents} />
          <div className="row">
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? 'Adding…' : 'Add endpoint'}
            </button>
            {!first && (
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
            )}
          </div>
          {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
        </form>
      ) : (
        <div>
          <button type="button" className="btn" onClick={() => setOpen(true)}>
            <Icon name="plus" size={14} width={2} />
            Add endpoint
          </button>
        </div>
      )}
    </>
  );
}

function EndpointCard({ endpoint: e }: { endpoint: WebhookEndpoint }) {
  const qc = useQueryClient();
  const now = useNow(5000);
  const [secret, setSecret] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [events, setEvents] = useState<string[]>(e.events);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const deliveries = useQuery({
    queryKey: ['webhook-deliveries', e.id],
    queryFn: () => api<{ data: WebhookDelivery[] }>(`/v1/webhooks/${e.id}/deliveries?limit=50`),
    refetchInterval: 5000,
  });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['webhooks'] }), qc.invalidateQueries({ queryKey: ['webhook-deliveries', e.id] })]);
  const act = async (fn: () => Promise<unknown>, fail: string) => {
    setMsg(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : fail);
    }
  };

  return (
    <section className="card" aria-label={`Webhook endpoint ${e.url}`}>
      <div className="card-head" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="row" style={{ gap: 8 }}>
            <span className={`badge ${e.enabled ? 'ok' : 'ended'}`}>
              <span className="dot" />
              {e.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <b className="mono" style={{ fontSize: 13, wordBreak: 'break-all' }}>{e.url}</b>
          </div>
          {!editing && (
            <div className="chips">
              {e.all_events ? <span className="chip">all events</span> : e.events.map((t) => <span key={t} className="chip">{t}</span>)}
            </div>
          )}
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn small" onClick={() => act(() => api(`/v1/webhooks/${e.id}/test`, { method: 'POST' }), 'Could not send.')}>
            Send test
          </button>
          <button type="button" className="btn small" onClick={() => setEditing((v) => !v)}>
            Events
          </button>
          <button type="button" className="btn small" onClick={() => act(() => api(`/v1/webhooks/${e.id}`, { method: 'PATCH', json: { enabled: !e.enabled } }), 'Could not change it.')}>
            {e.enabled ? 'Disable' : 'Enable'}
          </button>
          <button
            type="button"
            className="btn small"
            onClick={() => window.confirm(`Delete this endpoint and its delivery log?\n${e.url}`) && act(() => api(`/v1/webhooks/${e.id}`, { method: 'DELETE' }), 'Could not delete it.')}
          >
            Delete
          </button>
        </div>
      </div>

      {editing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <EventPicker types={Object.keys(EVENT_HELP)} value={events} onChange={setEvents} />
          <div className="row">
            <button
              type="button"
              className="btn small"
              disabled={!events.length}
              onClick={() => act(() => api(`/v1/webhooks/${e.id}`, { method: 'PATCH', json: { events } }), 'Could not save.').then(() => setEditing(false))}
            >
              Save events
            </button>
          </div>
        </div>
      )}

      {secret ? (
        <CopyField
          label="Signing secret"
          value={secret}
          oneLine
          secret
          tip="Verify every webhook with this before trusting it. After a rotation the old secret keeps signing too for 24 hours."
        />
      ) : (
        <div className="row">
          <button type="button" className="btn small" onClick={() => act(async () => setSecret((await api<WebhookEndpoint>(`/v1/webhooks/${e.id}`)).secret ?? null), 'Could not load it.')}>
            <Icon name="lock" size={12} />
            Show signing secret
          </button>
          <button
            type="button"
            className="btn small"
            onClick={() =>
              window.confirm('Make a new signing secret? The old one keeps working for 24 hours.') &&
              act(async () => setSecret((await api<WebhookEndpoint>(`/v1/webhooks/${e.id}/secret`, { method: 'POST' })).secret ?? null), 'Could not rotate it.')
            }
          >
            Rotate secret
          </button>
        </div>
      )}
      {msg && <p className="error-text" role="alert" style={{ margin: 0 }}>{msg}</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2 style={{ fontSize: 13 }}>
          Recent deliveries
          <Tip>Failed attempts are retried 10 times over about 24 hours. The log is kept for 30 days. Click a row to see what was sent.</Tip>
        </h2>
        {deliveries.isPending ? (
          <p className="muted">Loading…</p>
        ) : !deliveries.data?.data.length ? (
          <div className="table">
            <div className="empty">Nothing sent yet. Send a test, or start a stream.</div>
          </div>
        ) : (
          <div className="table deliveries" role="table" aria-label="Deliveries">
            <div className="tr th" role="row">
              <span role="columnheader">When</span>
              <span role="columnheader">Event</span>
              <span role="columnheader">Stream</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Last answer</span>
              <span role="columnheader" />
            </div>
            {deliveries.data.data.map((d) => (
              <Fragment key={d.id}>
                <div className={`tr${openRow === d.id ? ' open' : ''}`} role="row" style={{ cursor: 'pointer' }} onClick={() => setOpenRow(openRow === d.id ? null : d.id)}>
                  <span role="cell" className="muted">{when(d.created_at, now)}</span>
                  <span role="cell" className="cell-mono">{d.event_type}</span>
                  <span role="cell" className="cell-mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.stream_id ?? '—'}</span>
                  <span role="cell">
                    <span className={`badge ${d.status === 'succeeded' ? 'ok' : d.status === 'failed' ? 'bad' : 'pending'}`}>
                      <span className="dot" />
                      {d.status === 'succeeded' ? 'Delivered' : d.status === 'failed' ? 'Failed' : d.attempts ? 'Retrying' : 'Sending'}
                    </span>
                  </span>
                  <span role="cell" className="cell-mono" style={{ fontSize: 12 }}>
                    {d.last_error ?? (d.last_status_code ? `${d.last_status_code} in ${d.last_duration_ms} ms` : '—')}
                    {d.status === 'pending' && d.attempts > 0 && d.next_attempt_at ? ` · next ${when(d.next_attempt_at, now)}` : ''}
                    {d.attempts > 1 ? ` · ${d.attempts} tries` : ''}
                  </span>
                  <span role="cell" style={{ textAlign: 'right' }}>
                    {d.status !== 'succeeded' && (
                      <button
                        type="button"
                        className="btn small"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          act(() => api(`/v1/webhooks/${e.id}/deliveries/${d.id}/retry`, { method: 'POST' }), 'Could not retry.');
                        }}
                      >
                        Retry
                      </button>
                    )}
                  </span>
                </div>
                {openRow === d.id && <pre className="payload">{JSON.stringify(d.payload, null, 2)}</pre>}
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
